/**
 * Flight path for the DEV crow prop (`?crow`): take off from a perch, fly one
 * circle, land back on the same spot.
 *
 * Pure maths — no Three.js, no scene. `CrowProp` drives it and copies the sample
 * onto the object; `crowFlight.test.ts` pins the invariants that matter (the
 * bird must start and finish exactly on its perch, and never dip below it).
 *
 * Clip windows below come from measuring `root|TakeOff` in the source GLB:
 * 0–6.7 s the wings are motionless, 6.8–8.9 s is the flapping burst, 8.9–9.9 s
 * holds a glide pose, and 10.1–10.5 s folds the wings back to rest.
 */

/** Looping wingbeat, in seconds within the source clip. Tunable in the panel. */
export const FLAP_WINDOW = { start: 6.9, end: 8.9 };

/** Wings folding back to the perched pose — played once, on touchdown. */
export const FOLD_WINDOW = { start: 10.05, end: 10.52 };

export type Vector3Like = { x: number; y: number; z: number };

export interface CrowFlightParams {
  /** The perch. Both the take-off point and the landing point. */
  origin: Vector3Like;
  /** Circle radius in scene units. The circle passes through `origin`. */
  radius: number;
  /** Cruise height above `origin`. */
  height: number;
  /** Total flight time in seconds. */
  duration: number;
  /** `1` or `-1` — which way round the circle. */
  direction: number;
  /** Direction from the perch to the circle's centre, radians. */
  bearing: number;
  /** Peak roll into the turn, radians. */
  bank: number;
  /** Fraction of the flight spent climbing away from the perch. */
  climb: number;
  /** Fraction of the flight spent descending back onto it. */
  descend: number;
  /** Corrects the model's forward axis, radians. */
  yawOffset: number;
  /**
   * Radius modulation, 0–1. A constant radius reads as a drawn circle; letting
   * it bulge and pinch turns the loop into something a bird might actually fly.
   */
  wobble: number;
  /** Number of bulges around the loop. Whole numbers keep the path closed. */
  lobes: number;
  /** Vertical bob amplitude in scene units — the rise-and-sink of wingbeats. */
  bob: number;
  /** Bob oscillations over the whole flight. */
  bobCycles: number;
}

export interface CrowFlightSample {
  x: number;
  y: number;
  z: number;
  /** Yaw, radians — already includes `yawOffset`. */
  heading: number;
  /** Roll, radians. Zero on the ground, peaks in the cruise. */
  bank: number;
}

/**
 * A low, wide, unhurried sweep off the perch and back.
 *
 * Two proportions decide whether this reads as a bird or as a toy on a string,
 * and both were found the hard way:
 *
 * 1. **Turn rate is `360° / duration`** for a closed loop — radius does not
 *    enter into it. Above roughly 60°/s the bird visibly pirouettes, so a full
 *    circuit needs 6 s or more. Short-and-snappy is simply not available while
 *    the path is one closed loop.
 * 2. **Radius must beat body length.** The bird is ~0.35 units long; a radius
 *    below that has it turning about a point inside itself.
 */
export const DEFAULT_FLIGHT: Omit<CrowFlightParams, 'origin'> = {
  radius: 0.62,
  height: 0.12,
  duration: 7.5,
  direction: 1,
  bearing: 0,
  bank: 0.45,
  climb: 0.3,
  descend: 0.35,
  yawOffset: 0,
  wobble: 0.22,
  lobes: 2,
  bob: 0.012,
  bobCycles: 3,
};

/**
 * Named short flights. All of them stay near the perch — the point is a bird
 * doing bird things on a wall, not a patrol of the valley.
 *
 * `bearing` is inert here: `CrowProp.startFlight` derives it from whichever way
 * the bird is turned. Keep `bob` below `height` in any new preset, or the bob can
 * sink the bird through its own perch mid-loop — `crowFlight.test.ts` checks it.
 */
export const FLIGHT_PRESETS = {
  /** The lazy low sweep. Everything else here is a variation on it. */
  glide: { ...DEFAULT_FLIGHT },

  /** A proper turn around the place — bigger, a little higher, leaning harder. */
  wide: {
    ...DEFAULT_FLIGHT,
    radius: 0.95,
    height: 0.28,
    duration: 10,
    bank: 0.5,
    wobble: 0.2,
    bob: 0.015,
    bobCycles: 4,
  },

  /** Barely leaves the wall, weaving low along it. */
  skim: {
    ...DEFAULT_FLIGHT,
    radius: 0.75,
    height: 0.06,
    duration: 9,
    climb: 0.22,
    descend: 0.28,
    bank: 0.32,
    wobble: 0.35,
    lobes: 3,
    bob: 0.01,
    bobCycles: 5,
  },
} satisfies Record<string, Omit<CrowFlightParams, 'origin'>>;

export type FlightPresetName = keyof typeof FLIGHT_PRESETS;

export const DEFAULT_PRESET: FlightPresetName = 'glide';

const TWO_PI = Math.PI * 2;
const EPSILON = 1e-4;

const clamp01 = (value: number): number => Math.min(1, Math.max(0, value));

/** Ease with zero derivative at both ends — no visible kick at take-off. */
export const smoothstep = (t: number): number => t * t * (3 - 2 * t);

/** Shortest signed equivalent of an angle, in (-π, π]. */
export const wrapAngle = (radians: number): number =>
  Math.atan2(Math.sin(radians), Math.cos(radians));

/**
 * Sample the flight at normalised time `u` (0 = on the perch, 1 = back on it).
 *
 * The circle is built so `origin` lies ON it: the centre sits `radius` away
 * along `bearing`, and the sweep starts at the point diametrically opposite.
 * A full 2π sweep therefore returns to the perch exactly, with no seam to hide.
 */
export const sampleCrowFlight = (
  params: CrowFlightParams,
  u: number,
): CrowFlightSample => {
  const t = clamp01(u);
  const sweep = smoothstep(t);

  const centerX = params.origin.x + params.radius * Math.cos(params.bearing);
  const centerZ = params.origin.z + params.radius * Math.sin(params.bearing);
  const start = params.bearing + Math.PI;
  const theta = start + params.direction * TWO_PI * sweep;

  // Radius modulation keyed to θ, not to time: it is then periodic in 2π by
  // construction, so whole `lobes` leave the loop closed and the perch exact.
  const radius = params.radius * (1 + params.wobble * Math.sin(params.lobes * (theta - start)));

  // Climb and descent are separate ramps; taking the smaller of the two keeps
  // the bird on the ground at both ends whatever the two fractions add up to.
  const climb = smoothstep(clamp01(t / Math.max(params.climb, EPSILON)));
  const descend = smoothstep(clamp01((1 - t) / Math.max(params.descend, EPSILON)));
  const lift = Math.min(climb, descend);

  // The bob rides on `lift` too, so it fades out as the bird settles and cannot
  // push the landing off the perch.
  const bob = params.bob * Math.sin(TWO_PI * params.bobCycles * t) * lift;

  // Velocity is the tangent, so the bird always faces where it goes. With a
  // varying radius that is the full derivative, not just the circle's tangent —
  // dropping the dr/dθ term makes the heading lag visibly on the bulges.
  const dRadius =
    params.radius * params.wobble * params.lobes * Math.cos(params.lobes * (theta - start));
  const velocityX = params.direction * (dRadius * Math.cos(theta) - radius * Math.sin(theta));
  const velocityZ = params.direction * (dRadius * Math.sin(theta) + radius * Math.cos(theta));

  return {
    x: centerX + radius * Math.cos(theta),
    y: params.origin.y + params.height * lift + bob,
    z: centerZ + radius * Math.sin(theta),
    heading: Math.atan2(velocityX, velocityZ) + params.yawOffset,
    bank: params.direction * params.bank * lift,
  };
};

/**
 * The `bearing` that makes the loop open along `facing`, so the bird takes off
 * exactly where its head already points.
 *
 * The opening heading falls by precisely one radian per radian of bearing —
 * whichever way round the loop runs, and whatever the wobble does to the radius
 * — so the correction is a plain subtraction rather than a solve.
 */
export const bearingForFacing = (params: CrowFlightParams, facing: number): number =>
  wrapAngle(params.bearing + (sampleCrowFlight(params, 0).heading - facing));
